import { Close, Delete, Edit } from "@mui/icons-material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import {
  Alert,
  Box,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Button from "../../components/CommonButton";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import SearchBar from "../../components/SearchBar";
import axios from "../../config/axios";
import { getUserData } from "../../utils/utilities";
import AddAdminModal from "../users/addAdminModal";
import EditModal from "../users/editUserModal";

const initAlert = { status: false, msg: "", variant: "success" };

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const AdminUserList = () => {
  const role = getUserData().role;
  const [userList, setUserList] = useState([]);
  // search data
  const [search, setSearch] = useState("");
  // pagination data
  const [totalRows, setTotalRows] = useState(5);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  // alert data
  const [alert, setAlert] = useState(initAlert);
  // modals
  const [addAdminModal, setAddAdminModal] = useState(false);
  const [deleteConfirmationDialog, setDeleteConfirmationDialog] =
    useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [userData, setUserData] = useState({});
  // misc
  const [deleteProfile, setDeleteProfile] = useState({});

  const closeAlertMessage = () => {
    setAlert(initAlert);
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
  };

  const handleDeleteConfirmationClose = () => {
    setDeleteConfirmationDialog(false);
  };

  // API Services
  const getSuperAdminList = (search, rowsPerPage, page) => {
    const query = {
      pagination: true,
      search,
      rowsPerPage,
      page: page + 1,
      sortBy: orderBy,
      descending: order === "asc" ? false : true,
    };

    axios
      .get("/super-admin/list", { params: query })
      .then((res) => {
        if (res.status === 200) {
          setTotalRows(res.data.data?.total || 0);
          setUserList(res.data.data?.list || []);
        }
      })
      .catch((err) => console.log(err));
  };

  const toggleAdminUserStatus = (userId) => {
    let url, body;

    if (role === "SuperAdmin") {
      url = "super-admin/toggle-status";
      body = { adminId: userId };
    }

    axios
      .patch(url, body)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            getSuperAdminList(search, rowsPerPage, page);
            setAlert({
              status: true,
              msg: res.data.message,
              variant: "success",
            });
          } else {
            if (res.data.type === "error") {
              setAlert({
                status: true,
                msg: res.data.error?.message || "",
                variant: "error",
              });
            }
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const handleDeleteAdmin = (id) => {
    axios
      .delete(`super-admin/delete?id=${id}`)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            setAlert({
              status: true,
              msg: res.data.message,
              variant: "success",
            });
            setDeleteProfile({});
            getSuperAdminList(search, rowsPerPage, page);
          } else {
            if (res.data.type === "error") {
              setAlert({
                status: true,
                msg: res.data.error?.message || "",
                variant: "error",
              });
            }
          }
        }
      })
      .catch((err) => console.log({ err }));
  };

  const createSortHandler = (property) => (event) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const headCells = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: "Name",
      sortable: true,
    },
    {
      id: "email",
      numeric: false,
      disablePadding: true,
      label: "Email",
      sortable: true,
    },
    {
      id: "action",
      numeric: false,
      disablePadding: true,
      label: "Actions",
      sortable: false,
    },
  ];

  useEffect(() => {
    if (role === "SuperAdmin") {
      getSuperAdminList(search, rowsPerPage, page);
    }
  }, [rowsPerPage, page, order, orderBy]);

  return (
    <div>
      <Grid container rowSpacing={0}>
        <Grid xs={12} item sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: "20px" }} color="#474747">
            Admin Users Management
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          px={"25px"}
          sx={{ borderRadius: "10px", backgroundColor: "white", py: 3 }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="stretch"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box sx={{ display: "flex", alignItems: "stretch", gap: 2 }}>
              <SearchBar
                sx={{ width: "100%" }}
                onSearch={(search) => {
                  setSearch(search);
                  getSuperAdminList(search, rowsPerPage, page);
                }}
              />
            </Box>
            {role == "SuperAdmin" ? (
              <Grid sm={6} item sx={{ display: "flex", justifyContent: "end" }}>
                {role == "SuperAdmin" && (
                  <Button onClick={() => setAddAdminModal(true)}>
                    Add New Admin User
                  </Button>
                )}
              </Grid>
            ) : null}
          </Stack>

          <TableContainer component={Paper} elevation={0}>
            <Table aria-label="simple table" className={"custom-table br-0"}>
              <TableHead sx={{ backgroundColor: "#f4f6f9" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "80px",
                      minWidth: "80px",
                      textAlign: "center",
                    }}
                  >
                    No.
                  </TableCell>

                  {headCells.map((headCell) => (
                    <TableCell
                      size="small"
                      key={headCell.id}
                      align={headCell.numeric ? "right" : "left"}
                      sortDirection={orderBy === headCell.id ? order : false}
                    >
                      {headCell.sortable ? (
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : "asc"}
                          onClick={createSortHandler(headCell.id)}
                        >
                          {headCell.label}
                          {orderBy === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === "desc"
                                ? "sorted descending"
                                : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!userList.length ? (
                  <TableRow>
                    <TableCell size="small" colSpan={4}>
                      <Typography sx={{ width: "100%", textAlign: "center" }}>
                        No Data Available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  userList.map((user, ind) => (
                    <TableRow key={ind}>
                      <TableCell sx={{ textAlign: "center" }}>
                        {ind + 1}
                      </TableCell>
                      <TableCell size="small">{user.name}</TableCell>
                      <TableCell size="small">{user.email}</TableCell>
                      <TableCell size="small">
                        <Box style={{ display: "flex" }}>
                          <Switch
                            color="primary"
                            checked={!user.is_disabled}
                            onChange={() => toggleAdminUserStatus(user._id)}
                          />
                          <Tooltip title="Delete">
                            <IconButton
                              color="inherit"
                              sx={{ padding: 0, marginLeft: "5px" }}
                              onClick={() => {
                                setDeleteConfirmationDialog(true);
                                setDeleteProfile(user);
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              color="inherit"
                              sx={{ padding: 0, marginLeft: "5px" }}
                              onClick={() => {
                                setEditUserModal(true);
                                setUserData(user);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[
                      5,
                      10,
                      25,
                      { label: "All", value: -1 },
                    ]}
                    count={totalRows}
                    colSpan={6}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={alert.status}
        onClose={closeAlertMessage}
        message={alert.msg}
        key={"system-message"}
        autoHideDuration={2000}
      >
        <Alert severity={alert.variant}>
          {alert.msg}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            sx={{ ml: 2 }}
            onClick={closeAlertMessage}
          >
            <Close fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>
      {addAdminModal && (
        <AddAdminModal
          title="Add New Admin User"
          createSuperAdmin
          open={addAdminModal}
          setOpen={setAddAdminModal}
          setAlert={setAlert}
          getList={() => getSuperAdminList(search, rowsPerPage, page)}
        />
      )}

      {/* Delete Admin Dialog  */}
      <DeleteConfirmationDialog
        open={deleteConfirmationDialog}
        onDelete={() => {
          handleDeleteConfirmationClose();
          handleDeleteAdmin(deleteProfile._id);
        }}
        onClose={handleDeleteConfirmationClose}
        name={`user ${deleteProfile.name}`}
      />

      {/* Edit Admin Dialog  */}
      {editUserModal ? (
        <EditModal
          title="Update Admin User"
          data={userData}
          setData={setUserData}
          open={editUserModal}
          setOpen={setEditUserModal}
          alert={alert}
          setAlert={setAlert}
          getList={() => getSuperAdminList(search, rowsPerPage, page)}
        />
      ) : null}
    </div>
  );
};

export default AdminUserList;
